import * as THREE from 'three';
import type { ClubDefinition } from './Club';
import type { TrajectoryDefinition } from './Trajectory';

export interface LaunchCalibrationInput {
  readonly club: ClubDefinition;
  readonly power: number;
  readonly quality?: number;
  readonly aimRadians: number;
  readonly faceErrorRadians?: number;
  readonly trajectory?: TrajectoryDefinition;
  readonly attackLaunchDeltaDegrees?: number;
}

export interface LaunchConditions {
  readonly velocity: THREE.Vector3;
  readonly launchSpeedMetersPerSecond: number;
  readonly launchAngleDegrees: number;
  readonly backspinRpm: number;
  readonly expectedCarryYards: number;
  readonly rolloutResistanceMultiplier: number;
  readonly bounceRestitutionMultiplier: number;
}

const MIN_EFFECTIVE_POWER = 0.12;

/** Converts the selected club and swing power into testable launch conditions for the physics engine. */
export function calibrateLaunchConditions(input: LaunchCalibrationInput): LaunchConditions {
  const powerScalar = THREE.MathUtils.clamp(input.power / 100, 0, 1);
  const qualityScalar = THREE.MathUtils.clamp(input.quality ?? 1, 0.72, 1.05);
  const effectivePower = THREE.MathUtils.clamp(powerScalar * qualityScalar, MIN_EFFECTIVE_POWER, 1);
  const club = input.club;
  const rolloutTendency = THREE.MathUtils.clamp(club.rolloutTendency, 0, 1);
  const trajectory = input.trajectory;
  const launchSpeedMetersPerSecond =
    THREE.MathUtils.lerp(
      club.launch.minBallSpeedMetersPerSecond,
      club.launch.maxBallSpeedMetersPerSecond,
      Math.pow(effectivePower, 0.92),
    ) * (trajectory?.speedMultiplier ?? 1);
  const launchAngleDegrees = THREE.MathUtils.clamp(
    THREE.MathUtils.lerp(
      Math.max(1, club.launch.launchAngleDegrees - club.launch.powerLaunchDeltaDegrees),
      club.launch.launchAngleDegrees,
      effectivePower,
    ) +
      (trajectory?.launchDeltaDegrees ?? 0) +
      (input.attackLaunchDeltaDegrees ?? 0),
    0.5,
    62,
  );
  const launchAngle = THREE.MathUtils.degToRad(launchAngleDegrees);
  const faceErrorRadians = input.faceErrorRadians ?? 0;
  const aimDirection = new THREE.Vector3(
    Math.sin(input.aimRadians + faceErrorRadians),
    0,
    Math.cos(input.aimRadians + faceErrorRadians),
  ).normalize();
  const velocity = aimDirection.multiplyScalar(Math.cos(launchAngle) * launchSpeedMetersPerSecond);
  velocity.y = Math.sin(launchAngle) * launchSpeedMetersPerSecond;

  return {
    velocity,
    launchSpeedMetersPerSecond,
    launchAngleDegrees,
    backspinRpm:
      THREE.MathUtils.lerp(club.spin.backspinRpm * 0.75, club.spin.backspinRpm, effectivePower) *
      (trajectory?.spinMultiplier ?? 1),
    expectedCarryYards: club.expectedCarryYards,
    rolloutResistanceMultiplier:
      THREE.MathUtils.lerp(2.35, 0.58, rolloutTendency) * (trajectory?.rolloutMultiplier ?? 1),
    bounceRestitutionMultiplier:
      THREE.MathUtils.lerp(0.32, 1.2, rolloutTendency) * (trajectory?.bounceMultiplier ?? 1),
  };
}
